import React, { useEffect } from 'react';
import { useStorageStatus } from '../../hooks/useStorage';
import { storageApi } from '../../services/storageApi';
import { Cloud, HardDrive, CheckCircle, XCircle, RefreshCw, AlertCircle } from 'lucide-react';

export default function CloudStorageSettings() {
  const { status, loading, error, refresh } = useStorageStatus();

  // Handle OAuth redirect params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (
      params.get('google') === 'connected' ||
      params.get('onedrive') === 'connected' ||
      params.get('zoho') === 'connected'
    ) {
      refresh();
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [refresh]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="animate-spin text-indigo-500 w-8 h-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <span>{error}</span>
        <button onClick={refresh} className="ml-auto text-sm underline">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Cloud Storage</h2>
        <p className="mt-1 text-sm text-gray-500">
          Connect your cloud storage accounts to browse, upload, and sync compliance documents.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <ProviderCard
          provider="google"
          label="Google Drive"
          icon={<GoogleDriveIcon />}
          connected={status?.google.connected ?? false}
          email={status?.google.email}
          onConnect={() => storageApi.google.connect()}
          onDisconnect={async () => {
            await storageApi.google.disconnect();
            refresh();
          }}
          description="Store documents in your Google Drive with automatic client folder creation."
        />

        <ProviderCard
          provider="onedrive"
          label="Microsoft OneDrive"
          icon={<OneDriveIcon />}
          connected={status?.onedrive.connected ?? false}
          email={status?.onedrive.email}
          onConnect={() => storageApi.onedrive.connect()}
          onDisconnect={async () => {
            await storageApi.onedrive.disconnect();
            refresh();
          }}
          description="Sync documents to OneDrive and open files directly in Microsoft 365."
        />

        <ProviderCard
          provider="zoho"
          label="Zoho WorkDrive"
          icon={<ZohoWorkDriveIcon />}
          connected={status?.zoho?.connected ?? false}
          email={status?.zoho?.email}
          onConnect={() => storageApi.zoho.connect()}
          onDisconnect={async () => {
            await storageApi.zoho.disconnect();
            refresh();
          }}
          description="Store and sync compliance documents in Zoho WorkDrive with client folder automation."
        />
      </div>

      <div className="border border-gray-100 rounded-xl p-5 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Automatic Workflows</h3>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-indigo-400 flex-shrink-0" />
            New client → auto-create cloud folder structure
          </li>
          <li className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-indigo-400 flex-shrink-0" />
            Generated MCA form → auto-upload to client's folder
          </li>
          <li className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-indigo-400 flex-shrink-0" />
            Signed document uploaded → sync to connected cloud
          </li>
          <li className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-indigo-400 flex-shrink-0" />
            Daily backup at 2:00 AM IST
          </li>
        </ul>
      </div>
    </div>
  );
}

// ─── Provider Card ────────────────────────────────────────────────────────────

interface ProviderCardProps {
  provider: string;
  label: string;
  icon: React.ReactNode;
  connected: boolean;
  email?: string;
  description: string;
  onConnect: () => void;
  onDisconnect: () => void;
}

function ProviderCard({
  label, icon, connected, email, description, onConnect, onDisconnect,
}: ProviderCardProps) {
  const [busy, setBusy] = React.useState(false);

  const handle = async (fn: () => void | Promise<void>) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  return (
    <div className={`border rounded-xl p-5 bg-white shadow-sm transition-all ${connected ? 'border-green-200' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-50 border border-gray-100">
            {icon}
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm">{label}</p>
            {connected && email && (
              <p className="text-xs text-gray-500 truncate max-w-[160px]">{email}</p>
            )}
          </div>
        </div>
        {connected ? (
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
        ) : (
          <XCircle className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
        )}
      </div>

      <p className="mt-3 text-xs text-gray-500">{description}</p>

      <div className="mt-4">
        {connected ? (
          <button
            onClick={() => handle(onDisconnect)}
            disabled={busy}
            className="w-full text-sm font-medium text-red-600 border border-red-200 rounded-lg py-2 hover:bg-red-50 transition-colors disabled:opacity-50"
          >
            {busy ? 'Disconnecting…' : 'Disconnect'}
          </button>
        ) : (
          <button
            onClick={() => handle(onConnect)}
            disabled={busy}
            className="w-full text-sm font-medium text-white bg-indigo-600 rounded-lg py-2 hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            {busy ? 'Connecting…' : 'Connect'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Brand icons (inline SVG) ─────────────────────────────────────────────────

function GoogleDriveIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
      <path d="M8.267 14.68l-5.9 2.22L6.54 22h10.92l4.173-5.1-5.9-2.22H8.267z" fill="#0066DA" />
      <path d="M8.267 14.68L12 8.22l3.733 6.46H8.267z" fill="#00AC47" />
      <path d="M12 8.22L8.267 2H15.733L19.46 8.22H12z" fill="#EA4335" />
      <path d="M19.46 8.22l-3.727 6.46 4.173 5.1 3.734-6.46L19.46 8.22z" fill="#FFBA00" />
      <path d="M4.367 16.9l3.9-2.22 3.733 6.46H6.54l-2.173-4.24z" fill="#00832D" />
      <path d="M2.44 17.16L6.54 22H4.367L.633 15.54l1.807 1.62z" fill="#2684FC" />
    </svg>
  );
}

function OneDriveIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
      <path d="M9.9 10.5a5.5 5.5 0 0110.1 2.9A4 4 0 0120 21H6a4 4 0 01-1.4-7.7A5.5 5.5 0 019.9 10.5z" fill="#0078D4" />
      <path d="M6.5 12.5A4.5 4.5 0 0111 8a4.5 4.5 0 013.9 2.3A3 3 0 0114 16H6a3 3 0 01-.5-5.95z" fill="#28A8E0" opacity=".9" />
    </svg>
  );
}

function ZohoWorkDriveIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22">
      <path d="M12 2L2 7l10 5 10-5-10-5z" fill="#E42527" />
      <path d="M2 17l10 5 10-5" fill="none" stroke="#E42527" strokeWidth="2" />
      <path d="M2 12l10 5 10-5" fill="none" stroke="#E42527" strokeWidth="2" />
    </svg>
  );
}
